/**
 * GodModeIndicator — HUD pill reflecting the SYNCED, server-authoritative
 * `Player.godMode` schema field (the single source of truth).
 *
 * Why this exists: `/god` toggles `player.godMode` on the server schema
 * (ChatCommandHandler), and the CombatPipeline closer reads that same field on
 * the defender side (combatPeaceful.ts). But nothing client-side surfaced the
 * synced flag — there were three DIVERGENT local reps (PlayerManager's private
 * `godMode`, PretestLoader's URL-flag `godMode`, DevTools CheatToggles'
 * `helix_god_mode` storage), none of which reflect the authoritative state.
 * This indicator subscribes ONLY to the synced schema (via SERVER_STATE_UPDATE,
 * which NetworkSyncSystem re-emits on every local-player onChange), so it can
 * never lie about invincibility.
 *
 * Mirrors BiomeNexusReturn's body-portal pattern: GameUIOverlay mounts inside
 * #world-passthrough (pointer-events:auto), so a body-level portal floats the
 * pill above the game world. It's purely informational (pointer-events:none).
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { EventBus, EVENTS } from '../../../shared/EventBus';

export const GodModeIndicator: React.FC = () => {
  const [active, setActive] = useState(false);

  useEffect(() => {
    // SERVER_STATE_UPDATE carries the synced Colyseus PlayerSchemaView; its
    // `godMode` boolean is the authoritative field. Re-emitted on every
    // local-player schema onChange, so a `/god` toggle propagates here.
    return EventBus.getInstance().on(
      EVENTS.SERVER_STATE_UPDATE,
      ({ playerSchema }: { playerSchema?: { godMode?: boolean } }) => {
        setActive(Boolean(playerSchema?.godMode));
      },
    );
  }, []);

  if (!active) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-testid="god-mode-indicator"
      style={{
        position: 'fixed',
        // top:96 clears the class-switcher bar (top-center) and sits above the
        // BiomeNexusReturn button (top:172, left:18); horizontally centered.
        top: 96,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100000,
        pointerEvents: 'none',
        padding: '6px 16px',
        background: 'rgba(30, 24, 8, 0.9)',
        border: '1px solid #f1c40f',
        borderRadius: 14,
        color: '#ffe89a',
        fontFamily: 'Inter, sans-serif',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.03em',
        whiteSpace: 'nowrap',
        boxShadow: '0 4px 18px rgba(241, 196, 15, 0.35)',
        backdropFilter: 'blur(4px)',
      }}
    >
      🛡️ God Mode — invincible
    </div>,
    document.body,
  );
};
